/**
 * Live Research — fetches PUBLIC reference data (peer-reviewed psychology,
 * scholarly case-law/statute references) for the active charge type. It NEVER
 * transmits juror data: outgoing query strings are drawn from a fixed topic
 * vocabulary (LR_TOPICS) and are additionally screened by `lrGuard` so no case
 * name and no free text from juror records can reach a URL.
 *
 * Cache lives in its own IndexedDB database ('spatchai_library'), fully
 * separate from juror data (localStorage voirai_state).
 *
 * Faithful port of the monolith's Live-Research block. The privacy guard
 * (`LR_ALLOWED_QUERY` + `lrGuard`) is reproduced VERBATIM and must not be
 * weakened.
 */

import { store } from '@/app/store';
import { showToast } from '@/ui/overlays';
import { byId } from '@/ui/dom';
import { CASELAW } from '@/data/caselaw';
import { LR_TOPICS } from '@/data/live-research-topics';

/* eslint-disable @typescript-eslint/no-explicit-any -- external JSON payloads
   from Semantic Scholar / Crossref / eCFR / proxy are dynamically shaped. */

/* ── PRIVACY GUARD ─────────────────────────────────────────────
   Whitelist enforcement: outgoing query strings must match one of
   these patterns. Anything else is refused before fetch(). */
const LR_ALLOWED_QUERY = /^[A-Za-z0-9 .,§&()'\-\/"+:]{2,120}$/;

export function lrGuard(q: string): string {
  if (typeof q !== 'string' || !LR_ALLOWED_QUERY.test(q)) throw new Error('LiveResearch: query rejected by privacy guard');
  // Belt-and-braces: refuse if the string matches any juror name currently in state
  try {
    const jurors = (store.getState() && store.getState().jurors) || [];
    for (let i = 0; i < jurors.length; i++) {
      const n = (jurors[i].name || '').trim();
      if (n && n.length > 2 && q.toLowerCase().indexOf(n.toLowerCase()) !== -1) throw new Error('LiveResearch: juror data blocked from network');
    }
  } catch (e) { if (String((e as Error).message).indexOf('LiveResearch') === 0) throw e; }
  return q;
}

/* ── INDEXEDDB CACHE (separate DB from all juror data) ───────── */
const LR_DB_NAME = 'spatchai_library';
const LR_DB_VER = 1;
let lrDb: IDBDatabase | null = null;

interface CacheHit {
  key: string;
  data: any;
  fetchedAt: number;
}

interface FetchResult<T = unknown> {
  data: T;
  fetchedAt: number;
  fromCache: boolean;
  stale?: boolean;
  offline?: boolean;
  failed?: boolean;
}

function lrOpenDb(): Promise<IDBDatabase> {
  return new Promise(function (res, rej) {
    if (lrDb) return res(lrDb);
    const req = indexedDB.open(LR_DB_NAME, LR_DB_VER);
    req.onupgradeneeded = function (e) {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'key' });
    };
    req.onsuccess = function (e) { lrDb = (e.target as IDBOpenDBRequest).result; res(lrDb); };
    req.onerror = function () { rej(req.error); };
  });
}

function lrCacheGet(key: string): Promise<CacheHit | null> {
  return lrOpenDb().then(function (db) {
    return new Promise<CacheHit | null>(function (res) {
      const tx = db.transaction('cache', 'readonly').objectStore('cache').get(key);
      tx.onsuccess = function () { res(tx.result || null); };
      tx.onerror = function () { res(null); };
    });
  }).catch(function () { return null; });
}

function lrCachePut(key: string, data: any): Promise<boolean> {
  return lrOpenDb().then(function (db) {
    return new Promise<boolean>(function (res) {
      const tx = db.transaction('cache', 'readwrite').objectStore('cache')
        .put({ key: key, data: data, fetchedAt: Date.now() });
      tx.onsuccess = function () { res(true); };
      tx.onerror = function () { res(false); };
    });
  }).catch(function () { return false; });
}

export function lrClearLibrary(): Promise<boolean> {
  return lrOpenDb().then(function (db) {
    return new Promise<boolean>(function (res) {
      const tx = db.transaction('cache', 'readwrite').objectStore('cache').clear();
      tx.onsuccess = function () { res(true); }; tx.onerror = function () { res(false); };
    });
  }).catch(function () { return false; });
}

const LR_WEEK = 7 * 24 * 3600 * 1000;
function lrAgeLabel(ts: number): string {
  const d = new Date(ts);
  return 'Fetched ' + d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/* Cache-first, offline-aware fetch: return cached copy if available,
   prefer cache when offline, fetch live if online and cache is old/missing.
   Falls back to stale cache if network fails. */
function lrCachedFetch<T>(key: string, maxAge: number, fetcher: () => Promise<T>): Promise<FetchResult<T>> {
  return lrCacheGet(key).then(function (hit): FetchResult<T> | Promise<FetchResult<T>> {
    const isOnline = navigator.onLine;
    const isCacheValid = hit && (Date.now() - hit.fetchedAt) < maxAge;

    // Cache is valid and we have it — return immediately
    if (isCacheValid && hit) return { data: hit.data as T, fetchedAt: hit.fetchedAt, fromCache: true };

    // We're offline — use stale cache if available
    if (!isOnline) {
      if (hit) return { data: hit.data as T, fetchedAt: hit.fetchedAt, fromCache: true, stale: true, offline: true };
      throw new Error('Offline and no cached data available');
    }

    // We're online and cache is missing/stale — fetch fresh
    return fetcher().then(function (data): FetchResult<T> {
      lrCachePut(key, data);
      return { data: data, fetchedAt: Date.now(), fromCache: false };
    }).catch(function (err): FetchResult<T> {
      // Network failed — fall back to stale cache if available
      if (hit) return { data: hit.data as T, fetchedAt: hit.fetchedAt, fromCache: true, stale: true, failed: true };
      throw err;
    });
  });
}

/* ── OPTIONAL PROXY (Cloudflare Worker) ────────────────────────
   CourtListener requires an auth token (CORS-blocked from browsers),
   so live case-law search needs a personal proxy. Set the URL below
   after deploying the Worker (see README). Empty = feature hidden. */
const LR_PROXY_URL = '';   // e.g. 'https://spatchai-proxy.YOURNAME.workers.dev'

interface PsychPaper {
  title?: string;
  year?: number;
  abstract?: string;
  authors?: string;
  cites?: number;
  url?: string;
  doi?: string;
  journal?: string;
}

interface CasePaper {
  name?: string;
  court?: string;
  date?: string;
  cite?: string;
  url?: string | null;
  snippet?: string;
}

/* ── SOURCE 1: SEMANTIC SCHOLAR (CORS: yes, key: none) ────────── */
export function lrSearchPsych(topic: string): Promise<FetchResult<PsychPaper[]>> {
  lrGuard(topic);
  const key = 's2:' + topic;
  return lrCachedFetch(key, LR_WEEK, function () {
    const u = 'https://api.semanticscholar.org/graph/v1/paper/search?query=' + encodeURIComponent(topic) +
      '&fields=title,year,abstract,authors,externalIds,citationCount,url&limit=8';
    return fetch(u).then(function (r) {
      if (!r.ok) throw new Error('Semantic Scholar ' + r.status);
      return r.json();
    }).then(function (j: any): PsychPaper[] {
      return (j.data || []).map(function (p: any): PsychPaper {
        return {
          title: p.title, year: p.year, abstract: (p.abstract || '').slice(0, 600),
          authors: (p.authors || []).slice(0, 4).map(function (a: any) { return a.name; }).join(', '),
          cites: p.citationCount, url: p.url, doi: p.externalIds && p.externalIds.DOI
        };
      });
    });
  });
}

/* ── SOURCE 2: CROSSREF (CORS: yes, key: none) ────────────────── */
export function lrSearchCrossref(topic: string): Promise<FetchResult<PsychPaper[]>> {
  lrGuard(topic);
  const key = 'cr:' + topic;
  return lrCachedFetch(key, LR_WEEK, function () {
    const u = 'https://api.crossref.org/works?query=' + encodeURIComponent(topic) + '&rows=6&select=title,author,issued,DOI,container-title,is-referenced-by-count';
    return fetch(u).then(function (r) {
      if (!r.ok) throw new Error('Crossref ' + r.status);
      return r.json();
    }).then(function (j: any): PsychPaper[] {
      return ((j.message && j.message.items) || []).map(function (p: any): PsychPaper {
        return {
          title: (p.title || [''])[0],
          journal: (p['container-title'] || [''])[0],
          year: p.issued && p.issued['date-parts'] && p.issued['date-parts'][0] && p.issued['date-parts'][0][0],
          authors: (p.author || []).slice(0, 4).map(function (a: any) { return (a.given ? a.given + ' ' : '') + (a.family || ''); }).join(', '),
          cites: p['is-referenced-by-count'], doi: p.DOI
        };
      });
    });
  });
}

/* ── SOURCE 3: eCFR (CORS: yes, key: none) ─────────────────────── */
export function lrFetchCFR(title: number | string, part: number | string): Promise<FetchResult<{ title: number | string; part: number | string; text: string }>> {
  lrGuard(String(title) + ' ' + String(part));
  const key = 'ecfr:' + title + ':' + part;
  return lrCachedFetch(key, LR_WEEK, function () {
    const today = new Date().toISOString().slice(0, 10);
    const u = 'https://www.ecfr.gov/api/versioner/v1/full/' + today + '/title-' + title + '.xml?part=' + encodeURIComponent(String(part));
    return fetch(u).then(function (r) {
      if (!r.ok) throw new Error('eCFR ' + r.status);
      return r.text();
    }).then(function (xml) {
      const doc = new DOMParser().parseFromString(xml, 'text/xml');
      const txt = ((doc.documentElement && doc.documentElement.textContent) || '').replace(/\s+/g, ' ').trim();
      return { title: title, part: part, text: txt.slice(0, 20000) };
    });
  });
}

/* ── SOURCE 4: COURTLISTENER via personal proxy (token required) ── */
export function lrSearchCaseLaw(topic: string, courts?: string): Promise<FetchResult<CasePaper[]>> {
  lrGuard(topic);
  if (!LR_PROXY_URL) return Promise.reject(new Error('proxy-not-configured'));
  const key = 'cl:' + topic + ':' + (courts || '');
  // Case law cached 30 days — CourtListener free tier is 125 req/day, so never refetch needlessly
  return lrCachedFetch(key, 30 * 24 * 3600 * 1000, function () {
    const u = LR_PROXY_URL + '/cl/search?q=' + encodeURIComponent(topic) + '&type=o&order_by=score+desc' +
      (courts ? ('&court=' + encodeURIComponent(courts)) : '');
    return fetch(u).then(function (r) {
      if (!r.ok) throw new Error('CourtListener proxy ' + r.status);
      return r.json();
    }).then(function (j: any): CasePaper[] {
      return (j.results || []).slice(0, 8).map(function (o: any): CasePaper {
        return {
          name: o.caseName, court: o.court, date: o.dateFiled, cite: (o.citation || []).join('; '),
          url: o.absolute_url ? ('https://www.courtlistener.com' + o.absolute_url) : null,
          snippet: (o.snippet || '').replace(/<[^>]+>/g, '').slice(0, 400)
        };
      });
    });
  });
}

/* ── SOURCE 5: U.S. CODE section via GovInfo through proxy, with
     bundled fallback text for the mapped sections ─────────────── */
export function lrFetchUSC(title: number | string, section: string): Promise<FetchResult<unknown>> {
  lrGuard(title + ' USC ' + section);
  const key = 'usc:' + title + ':' + section;
  if (!LR_PROXY_URL) {
    return lrCacheGet(key).then(function (hit): FetchResult<unknown> {
      if (hit) return { data: hit.data, fetchedAt: hit.fetchedAt, fromCache: true };
      throw new Error('proxy-not-configured');
    });
  }
  return lrCachedFetch(key, 30 * 24 * 3600 * 1000, function () {
    const u = LR_PROXY_URL + '/usc?title=' + title + '&section=' + encodeURIComponent(section);
    return fetch(u).then(function (r) {
      if (!r.ok) throw new Error('USC proxy ' + r.status);
      return r.json();
    });
  });
}

/* ── PREBAKED MA STATUTE LIBRARY (data/ma-statutes.json) ────────
   Generated by tools/prebake-statutes.mjs via GitHub Action from
   malegislature.gov. Loaded same-origin: no CORS, works offline once
   the service worker caches it. When present, its text REPLACES the
   bundled fullText of matching statute entries, with provenance. */
interface StatuteSection {
  citation: string;
  text: string;
  url: string;
}
interface StatuteLibrary {
  generated: string;
  sections: StatuteSection[];
}
export let lrStatuteLib: StatuteLibrary | null = null;

function lrNormCite(c: string): string { return String(c || '').toLowerCase().replace(/[\s§,.]+/g, ''); }

export function lrLoadStatuteLibrary(): Promise<number> {
  return fetch('./data/ma-statutes.json').then(function (r) {
    if (!r.ok) throw new Error('no prebaked statutes');
    return r.json();
  }).then(function (j: StatuteLibrary): number {
    lrStatuteLib = j;
    if (!(j && j.sections && j.sections.length)) return 0;
    const byCite: Record<string, StatuteSection> = {};
    j.sections.forEach(function (s) { byCite[lrNormCite(s.citation)] = s; });
    let merged = 0;
    try {
      CASELAW.forEach(function (item) {
        if (item.type !== 'statute') return;
        const hit = byCite[lrNormCite(item.citation)];
        if (hit) {
          item.fullText = hit.citation + '\n\n' + hit.text +
            '\n\n[Source: ' + hit.url + ' — unofficial online version, retrieved ' +
            String(j.generated).slice(0, 10) + '. Verify against the official edition before citing.]';
          (item as unknown as { verifiedSource?: boolean }).verifiedSource = true;
          merged++;
        }
      });
    } catch (e) { void e; }
    return merged;
  }).catch(function () { return 0; });
}

/* ── AGGREGATE: fetch everything relevant for the active charges ── */
interface RenderEntry {
  kind: string;
  topic: string;
  res: { data: any[]; fetchedAt: number; stale?: boolean } | null;
}
type Job = Promise<{ kind: string; topic: string; res: FetchResult<unknown> | null }>;

export function lrFetchAllForCharges(chargeKeys: string[], onProgress?: (done: number, total: number) => void): Promise<Array<{ kind: string; topic: string; res: FetchResult<unknown> | null }>> {
  const psychQ: string[] = [];
  const caseQ: string[] = [];
  chargeKeys.forEach(function (k) {
    const t = LR_TOPICS[k]; if (!t) return;
    t.psych.forEach(function (q) { if (psychQ.indexOf(q) === -1) psychQ.push(q); });
    t.caselaw.forEach(function (q) { if (caseQ.indexOf(q) === -1) caseQ.push(q); });
  });
  let done = 0; const total = psychQ.length * 2 + (LR_PROXY_URL ? caseQ.length : 0);
  function tick() { done++; if (onProgress) onProgress(done, total); }
  const jobs: Job[] = [];
  psychQ.forEach(function (q) {
    jobs.push(lrSearchPsych(q).catch(function () { return null; }).then(function (r) { tick(); return { kind: 'psych', topic: q, res: r }; }));
    jobs.push(lrSearchCrossref(q).catch(function () { return null; }).then(function (r) { tick(); return { kind: 'psych-cr', topic: q, res: r }; }));
  });
  if (LR_PROXY_URL) {
    caseQ.forEach(function (q) {
      jobs.push(lrSearchCaseLaw(q, 'mass massappct ca1 mad').catch(function () { return null; }).then(function (r) { tick(); return { kind: 'case', topic: q, res: r }; }));
    });
  }
  return Promise.all(jobs);
}

/* ── RENDER / FETCH (research DOM) ────────────────────────────── */
function lrEsc(s: unknown): string { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

function lrRenderPapers(items: RenderEntry[], out: string[]): void {
  items.forEach(function (entry) {
    if (!entry || !entry.res || !entry.res.data || !entry.res.data.length) return;
    const stamp = lrAgeLabel(entry.res.fetchedAt) + (entry.res.stale ? ' — offline, may be outdated' : '');
    out.push('<div style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin:10px 0 4px;">' + lrEsc(entry.topic) + ' <span style="font-weight:400;text-transform:none;">(' + stamp + ')</span></div>');
    entry.res.data.slice(0, 4).forEach(function (p: any) {
      if (entry.kind === 'case') {
        out.push('<div class="ritem"><strong>' + lrEsc(p.name) + '</strong>' + (p.cite ? ', ' + lrEsc(p.cite) : '') + (p.date ? ' (' + lrEsc(String(p.date).slice(0, 4)) + ')' : '') +
          (p.snippet ? '<br><span style="color:#64748b;">' + lrEsc(p.snippet) + '</span>' : '') +
          (p.url ? ' <a href="' + lrEsc(p.url) + '" target="_blank" rel="noopener">View</a>' : '') + '</div>');
      } else {
        out.push('<div class="ritem"><strong>' + lrEsc(p.title) + '</strong>' + (p.authors ? ' — ' + lrEsc(p.authors) : '') + (p.year ? ' (' + p.year + ')' : '') +
          (p.journal ? ', <em>' + lrEsc(p.journal) + '</em>' : '') +
          (typeof p.cites === 'number' ? ' · ' + p.cites + ' citations' : '') +
          (p.abstract ? '<br><span style="color:#64748b;">' + lrEsc(p.abstract.slice(0, 280)) + (p.abstract.length > 280 ? '…' : '') + '</span>' : '') +
          (p.doi ? ' <a href="https://doi.org/' + lrEsc(p.doi) + '" target="_blank" rel="noopener">DOI</a>' : (p.url ? ' <a href="' + lrEsc(p.url) + '" target="_blank" rel="noopener">View</a>' : '')) + '</div>');
      }
    });
  });
}

export function renderCachedLiveResearch(): void {
  const el = byId('live-research-results'); if (!el) return;
  const t = LR_TOPICS[store.getState().researchCharge]; if (!t) { el.innerHTML = ''; return; }
  const jobs: Promise<RenderEntry | null>[] = [];
  t.psych.forEach(function (q) {
    jobs.push(lrCacheGet('s2:' + q).then(function (hit) { return hit ? { kind: 'psych', topic: q, res: { data: hit.data, fetchedAt: hit.fetchedAt } } : null; }));
  });
  t.caselaw.forEach(function (q) {
    jobs.push(lrCacheGet('cl:' + q + ':mass massappct ca1 mad').then(function (hit) { return hit ? { kind: 'case', topic: q, res: { data: hit.data, fetchedAt: hit.fetchedAt } } : null; }));
  });
  Promise.all(jobs).then(function (items) {
    const out: string[] = []; lrRenderPapers(items.filter(Boolean) as RenderEntry[], out);
    if (out.length) el.innerHTML = '<div style="font-size:11px;color:#94a3b8;margin-bottom:2px;">Showing cached results — works offline. Attribution: Semantic Scholar, Crossref, CourtListener.</div>' + out.join('');
  });
}

export function fetchLiveResearch(): void {
  const t = LR_TOPICS[store.getState().researchCharge]; if (!t) { showToast('No live topics for this charge'); return; }
  if (!navigator.onLine) { showToast('Offline — showing cached results only'); renderCachedLiveResearch(); return; }
  const btn = byId<HTMLButtonElement>('live-fetch-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Fetching…'; }
  const jobs: Promise<RenderEntry | null>[] = [];
  t.psych.forEach(function (q) {
    jobs.push(lrSearchPsych(q).catch(function () { return null; }).then(function (r) { return r ? { kind: 'psych', topic: q, res: r } : null; }));
    jobs.push(lrSearchCrossref(q).catch(function () { return null; }).then(function (r) { return r ? { kind: 'psych', topic: q + ' (Crossref)', res: r } : null; }));
  });
  if (LR_PROXY_URL) {
    t.caselaw.forEach(function (q) {
      jobs.push(lrSearchCaseLaw(q, 'mass massappct ca1 mad').catch(function () { return null; }).then(function (r) { return r ? { kind: 'case', topic: q, res: r } : null; }));
    });
  }
  Promise.all(jobs).then(function (items) {
    const out: string[] = []; lrRenderPapers(items.filter(Boolean) as RenderEntry[], out);
    const el = byId('live-research-results');
    if (el) el.innerHTML = (out.length ? '<div style="font-size:11px;color:#94a3b8;margin-bottom:2px;">Results cached for offline use. Attribution: Semantic Scholar, Crossref' + (LR_PROXY_URL ? ', CourtListener' : '') + '.</div>' + out.join('') : '<div class="ritem">No results returned. Check your connection and try again.</div>');
    if (!LR_PROXY_URL && el) el.innerHTML += '<div style="font-size:11px;color:#94a3b8;margin-top:6px;">Live case-law search requires the personal proxy (see README) — psychology research works without it.</div>';
    if (btn) { btn.disabled = false; btn.textContent = 'Fetch latest research for this charge'; }
    showToast('Research updated');
  });
}
